import { EqptType, Equipment } from 'domains/eqptDomain';
import { BUSINESS_TYPE_ENUM } from 'domains/gallerySkuDomain';
import { JobOptionSvcGroup } from 'domains/jobconfigDomain';
import {
  ParcelCategoriesEnum,
  PartialSKU,
  QtyDependencyDisplayMap,
  QtyDependencyEnum,
  SKUPart,
} from 'domains/skuDomain';
import { MeasurementUnitEnum, MeasureType } from 'domains/measurementDomain';
import i18n from 'i18next';
import { sortBy } from 'lodash';
import { isEqual } from 'lodash-es';
import { isMetric, unitAbbrev } from './measurement';

/**
 * One measurement in the unit the reviewer is working in, with that unit spelled after it.
 *
 * The publish review is read as plain text with nothing else on the line to say what the numbers
 * are, so an 8.5x11 next to a 215.9x279.4 is only meaningful once each says which system it is in.
 *
 * The unit label is decided by which value was actually used, never by the viewer's setting: a row
 * whose metric twin is missing falls back to the imperial one and is labelled `in`, rather than
 * being relabelled `mm` while still holding inches.
 *
 * The unit is a parameter rather than a module-level read, all the way up to `computeSkuDiffs`,
 * because the diffs are built once into a `useMemo`. Reading a global here left the strings frozen
 * in whatever unit the page was opened in: switching units changed nothing until a reload. As an
 * argument it has to appear in the caller's dependency list, so the memo recomputes.
 */
const shownMeasure = (
  imperial: string | number | null | undefined,
  metric: string | number | null | undefined,
  type: MeasureType,
  unit: MeasurementUnitEnum
): string => {
  const useMetric =
    isMetric(unit) &&
    metric !== null &&
    metric !== undefined &&
    metric !== '';
  const value = useMetric ? metric : imperial;
  if (value === null || value === undefined || value === '') {
    return '';
  }
  return `${value} ${unitAbbrev(
    type,
    useMetric ? MeasurementUnitEnum.METRIC : MeasurementUnitEnum.IMPERIAL
  )}`;
};

export const topGalleryLabels: { [x in BUSINESS_TYPE_ENUM]?: string } = {
  [BUSINESS_TYPE_ENUM.BUSINESS_ESSENTIALS]: 'Business Essentials',
  [BUSINESS_TYPE_ENUM.DIRECT_MAIL]: 'Direct Mail',
  [BUSINESS_TYPE_ENUM.MARKETING]: 'Marketing',
  [BUSINESS_TYPE_ENUM.MY_COMPANY]: 'My Company',
  [BUSINESS_TYPE_ENUM.MY_PRODUCTS]: 'My Products',
  [BUSINESS_TYPE_ENUM.PROMOTIONAL_ITEMS]: 'Promo Items',
};

export const parcelCategoriesList = [
  { title: 'Flat Carton', value: ParcelCategoriesEnum.FC },
  { title: 'Large Carton', value: ParcelCategoriesEnum.LC },
  { title: 'Standard Carton', value: ParcelCategoriesEnum.SC },
  { title: 'Tube', value: ParcelCategoriesEnum.T },
  { title: 'Carrier Parcel', value: ParcelCategoriesEnum.CP },
];

export const getQtyRulesString = (sku: PartialSKU) => {
  const items = [];

  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.defaultQuantity'),
    sku.defaultQty,
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.defaultSides'),
    sku.sides,
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.defaultPageCount'),
    sku.defaultPageCount,
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.orientations'),
    (sku.orientation || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.wizard.quantity.form.label.pageCountTip'),
    String(sku.pageCountEditable),
  ]);

  return items.map((item) => item.join(': ')).join('\n');
};

export const getQtyDependencyString = (
  skuPart?: SKUPart,
  topSku?: PartialSKU
) => {
  const items = [];
  if (skuPart) {
    items.push([
      i18n.t('platform.sku.publishReview.optionTitle.qtyDependency'),
      QtyDependencyDisplayMap[skuPart.qtyDependency.qtyDependencyEnum],
    ]);
    if (
      skuPart.qtyDependency.qtyDependencyEnum !== QtyDependencyEnum.SPECIFIC
    ) {
      const dependOnSku = topSku
        ? (topSku.parts || []).find(
            (part) => part.part.id === skuPart.qtyDependency.dependsOnSkuId
          )?.part
        : null;
      items.push([
        i18n.t('platform.sku.wizard.quantity.form.label.dependsOnSkuId'),
        dependOnSku?.name || skuPart.qtyDependency.dependsOnSkuId,
      ]);
    } else {
      items.push([
        i18n.t('platform.sku.publishReview.optionTitle.defaultQuantity'),
        skuPart.qtyDependency.defaultQty,
      ]);
    }
  }

  return items.map((item) => item.join(': ')).join('\n');
};

export const getMultiQtyRulesString = (sku: PartialSKU) => {
  const items = [];
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.defaultQuantity'),
    sku.defaultQty,
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.orientations'),
    (sku.orientation || []).join(', '),
  ]);

  return items.map((item) => item.join(': ')).join('\n');
};

export const getSizesString = (sku: PartialSKU, unit: MeasurementUnitEnum) => {
  const items: string[] = [];
  items.push('Custom Rule Code: ' + sku.customRuleCode);
  sku.skuSizes?.forEach((size) => {
    const item = [
      [
        i18n.t('platform.sku.publishReview.optionTitle.finishedSize'),
        shownMeasure(
          size.finishedSize,
          size.finishedSizeMetric,
          MeasureType.LENGTH,
          unit
        ),
      ],
      [
        i18n.t('platform.sku.publishReview.optionTitle.flatSize'),
        shownMeasure(
          size.flatSize,
          size.flatSizeMetric,
          MeasureType.LENGTH,
          unit
        ),
      ],
      [
        i18n.t('platform.sku.publishReview.optionTitle.orientation'),
        size.orientation,
      ],
    ];

    items.push(item.map((i) => i.join(': ')).join(', '));
  });

  items.push('Allow custom size: ' + sku.customSizeEnable);
  items.push(
    'Bleed for all sizes: ' +
      shownMeasure(
        sku.defaultBleedSizeIn,
        sku.defaultBleedSizeMm,
        MeasureType.LENGTH,
        unit
      )
  );

  return items.join('\n');
};

/**
 * The ready-made sizes this SKU offers, both catalogues.
 *
 * Unit-independent on purpose: the two selections are both part of the SKU whichever unit the
 * reviewer happens to be working in, and a publish changes both or neither.
 *
 * "None" rather than a blank line for an empty catalogue, so clearing a selection reads as a
 * change rather than as an empty row.
 */
export const getPresetSizesString = (sku: PartialSKU) => {
  const shown = (names: string[] | undefined) =>
    names && names.length ? names.join(', ') : 'None';
  return [
    `Metric: ${shown(sku.presetSizesMetric)}`,
    `Imperial: ${shown(sku.presetSizesImperial)}`,
  ].join('\n');
};

export const getPaperRulesString = (sku: PartialSKU) => {
  const items = [];
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.paperCompany'),
    (sku?.defaultPaperManufacturerName || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.category'),
    (sku?.defaultPaperCategory || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.basisWeight'),
    (sku?.defaultPaperBasisWeight || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.brand'),
    (sku?.defaultPaperName || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.color'),
    (sku?.defaultPaperColor || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.finish'),
    sku?.defaultPaperFinish,
  ]);
  items.push([
    i18n.t('platform.sku.wizard.paper.form.label.paperCount'),
    sku?.defaultPaperCount,
  ]);

  return items.map((item) => item.join(': ')).join('\n');
};

export const getPressesString = (
  sku: PartialSKU,
  eqptTypes: EqptType[],
  exceptEqpts: Equipment[]
) => {
  if (
    sku?.equipmentCodes?.length === eqptTypes.length &&
    sku?.exceptEquipmentIds?.length === 0
  ) {
    return i18n.t('platform.sku.form.tip.noPressExceptions');
  }
  let items = [i18n.t('platform.sku.form.label.press') + ':'];

  const presses = (sku.equipmentCodes || []).map((eqptCode) => {
    const eqptType = eqptTypes.find((item) => item.code === eqptCode);
    return `${eqptType?.name} (${eqptType?.code})`;
  });

  items = items.concat(presses);
  items = items.concat([i18n.t('platform.sku.exceptions') + ':']);
  const excepts = exceptEqpts.map((item) => item.name);
  items = items.concat(excepts);

  return items.join('\n');
};

export const getInkString = (sku: PartialSKU) => {
  const items = [];
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.coatingFront'),
    (sku?.coatingFront || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.coatingBack'),
    (sku?.coatingBack || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.inkFront'),
    (sku?.inkFront || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.inkBack'),
    (sku?.inkBack || []).join(', '),
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.hasPmsFront'),
    sku.hasPmsFront,
  ]);
  items.push([
    i18n.t('platform.sku.publishReview.optionTitle.hasPmsBack'),
    sku.hasPmsBack,
  ]);

  return items.map((item) => item.join(': ')).join('\n');
};

export const getRequireSvcString = (sku: PartialSKU) => {
  const items = [];
  if ((sku.requiredSvcTypes || []).length > 0) {
    items.push(i18n.t('platform.sku.serviceTypes') + ':');
    (sku.requiredSvcTypes || []).forEach((svcType) => {
      items.push('    ' + svcType.name);
    });

    if ((sku.exceptRequiredSvcs || []).length > 0) {
      items.push(i18n.t('platform.sku.exceptions') + ':');
      (sku.exceptRequiredSvcs || []).forEach((svc) => {
        items.push('    ' + svc.name);
      });
    }
  }

  if ((sku.requiredSvcs || []).length > 0) {
    items.push(i18n.t('platform.sku.services') + ':');

    (sku.requiredSvcs || []).forEach((svc) => {
      items.push('    ' + svc.name);
    });
  }

  return items.join('\n');
};

export const getOptionalSvcString = (sku: PartialSKU) => {
  const items = [];
  if ((sku.optionalSvcTypes || []).length > 0) {
    items.push(i18n.t('platform.sku.serviceTypes') + ':');
    (sku.optionalSvcTypes || []).forEach((svcType) => {
      items.push('    ' + svcType.name);
    });
  }

  return items.join('\n');
};

export const getExtraSpecString = (sku: PartialSKU) => {
  const items: string[] = [];
  if ((sku.extraSpecs || []).length > 0) {
    (sku.extraSpecs || []).forEach((spec) => {
      items.push(Object.keys(spec)[0] + ': ' + Object.values(spec)[0]);
    });
  }

  return items.join('\n');
};

export const getPartsString = (sku: PartialSKU) => {
  const items: string[] = [];
  items.push(
    i18n.t('platform.sku.publishReview.optionTitle.individualSKUs') + ':'
  );
  const parts = sortBy(sku.parts || [], (item) => item.sequence);
  parts.forEach((part, index) => {
    items.push(part?.part?.name);
  });

  return items.join('\n');
};

export const getSKUSizePairString = (
  skuPart: SKUPart,
  unit: MeasurementUnitEnum
) => {
  const items: string[] = [];
  (skuPart.skuSizePairs || []).forEach((pairItem) => {
    const item = [
      [
        i18n.t('platform.sku.wizard.size.form.label.mainSkuSize') + '',
        shownMeasure(
          pairItem.wrapperSize.finishedSize,
          pairItem.wrapperSize.finishedSizeMetric,
          MeasureType.LENGTH,
          unit
        ) + `(${pairItem?.wrapperSize?.orientation})`,
      ],
      [
        i18n.t('platform.sku.publishReview.optionTitle.individualSKUSize'),
        shownMeasure(
          pairItem?.partSize?.finishedSize,
          pairItem?.partSize?.finishedSizeMetric,
          MeasureType.LENGTH,
          unit
        ) + `(${pairItem?.partSize?.orientation})`,
      ],
    ];

    items.push(item.map((i) => i.join(': ')).join(', '));
  });

  return items.join('\n');
};

export const showSvcNameList = (jobOptionSvcGroup?: JobOptionSvcGroup[]) => {
  return jobOptionSvcGroup
    ?.map((item) => [
      ...item.svcs.map((svc) => svc.name),
      ...item.svcGroups.map((svcGroup) => svcGroup.svcs.map((svc) => svc.name)),
    ])
    .flat(2)
    .join(', ');
};

export type DiffInfo = {
  name: string;
  title: string;
  main?: string | boolean | BUSINESS_TYPE_ENUM[] | number;
  draft?: string | boolean | BUSINESS_TYPE_ENUM[] | number;
};
export const computeSkuDiffs = (
  sku: PartialSKU,
  mainSku: PartialSKU | undefined,
  eqptTypes: EqptType[],
  exceptEqpts: Equipment[],
  mainExceptEqpts: Equipment[],
  isTop: boolean,
  /**
   * The unit the reviewer is working in. Required, so that a caller cannot forget it and leave
   * the diff frozen in whatever unit its memo first ran under.
   */
  unit: MeasurementUnitEnum
): DiffInfo[] => {
  const diffArr: DiffInfo[] = [];

  if (!mainSku) {
    return [];
  }

  const artFiles = sku.artworks
    ?.map((item) => item.url)
    .filter((item) => item)
    .join(',');
  const mainArtFiles = mainSku?.artworks
    ?.map((item) => item.url)
    .filter((item) => item)
    .join(',');

  // name
  if (sku.name !== mainSku.name) {
    diffArr.push({
      name: 'name',
      title: i18n.t('platform.sku.publishReview.optionTitle.skuName'),
      main: mainSku.name,
      draft: sku.name,
    });
  }

  // pmsFrontEditable
  if (sku.pmsFrontEditable !== mainSku.pmsFrontEditable) {
    diffArr.push({
      name: 'pmsFrontEditable',
      title: i18n.t('platform.sku.publishReview.optionTitle.pmsFrontEditable'),
      main: Boolean(mainSku.pmsFrontEditable) + '',
      draft: Boolean(sku.pmsFrontEditable) + '',
    });
  }

  // pmsBackEditable
  if (sku.pmsBackEditable !== mainSku.pmsBackEditable) {
    diffArr.push({
      name: 'pmsBackEditable',
      title: i18n.t('platform.sku.publishReview.optionTitle.pmsBackEditable'),
      main: Boolean(mainSku.pmsBackEditable) + '',
      draft: Boolean(sku.pmsBackEditable) + '',
    });
  }

  // pmsFront
  if (!isEqual(sku?.pmsFront, mainSku?.pmsFront)) {
    diffArr.push({
      name: 'pmsFront',
      title: i18n.t('platform.sku.publishReview.optionTitle.pmsFront'),
      main: mainSku.pmsFront?.join(', ') || 'None',
      draft: sku.pmsFront?.join(', ') || 'None',
    });
  }

  // pmsBack
  if (!isEqual(sku?.pmsBack, mainSku?.pmsBack)) {
    diffArr.push({
      name: 'pmsBack',
      title: i18n.t('platform.sku.publishReview.optionTitle.pmsBack'),
      main: mainSku.pmsBack?.join(', ') || 'None',
      draft: sku.pmsBack?.join(', ') || 'None',
    });
  }

  // description
  if (sku.description !== mainSku.description) {
    diffArr.push({
      name: 'description',
      title: i18n.t('platform.sku.form.label.skuDesctiption'),
      main: mainSku.description,
      draft: sku.description,
    });
  }

  // active
  if (sku.active !== mainSku.active) {
    diffArr.push({
      name: 'active',
      title: i18n.t('platform.sku.form.label.active'),
      main: mainSku.active,
      draft: sku.active,
    });
  }

  // jobManualSvcs
  if (!isEqual(sku.jobManualSvcs, mainSku.jobManualSvcs)) {
    diffArr.push({
      name: 'jobManualSvcs',
      title: i18n.t('platform.sku.form.label.manualSvcs'),
      main:
        mainSku.jobManualSvcs?.map((item) => item.name).join(', ') || 'None',
      draft: sku.jobManualSvcs?.map((item) => item.name).join(', ') || 'None',
    });
  }

  // sku type
  if (sku.skuType?.id !== mainSku.skuType?.id) {
    diffArr.push({
      name: 'skuType',
      title: i18n.t('platform.sku.form.label.skuType'),
      main: mainSku.skuType?.name,
      draft: sku.skuType?.name,
    });
  }

  // tax code
  if (sku.taxCode !== mainSku.taxCode) {
    diffArr.push({
      name: 'taxCode',
      title: i18n.t('platform.sku.form.label.taxCode'),
      main: mainSku.taxCode,
      draft: sku.taxCode,
    });
  }

  // displayName
  if (sku.displayName !== mainSku.displayName) {
    diffArr.push({
      name: 'displayName',
      title: i18n.t('platform.sku.form.label.displayName'),
      main: mainSku.displayName,
      draft: sku.displayName,
    });
  }

  // displayNamePlural
  if (sku.displayNamePlural !== mainSku.displayNamePlural) {
    diffArr.push({
      name: 'displayNamePlural',
      title: i18n.t('platform.sku.form.label.displayNamePlural'),
      main: mainSku.displayNamePlural,
      draft: sku.displayNamePlural,
    });
  }

  // directlyAddToCart
  if (sku.preConfigured !== mainSku.preConfigured) {
    diffArr.push({
      name: 'preConfigured',
      title: i18n.t('platform.sku.form.label.directlyAddToCart'),
      main: Boolean(mainSku.preConfigured) ? 'True' : 'False',
      draft: Boolean(sku.preConfigured) ? 'True' : 'False',
    });
  }

  // hideFreightCost
  if (Boolean(sku.hideFreightCost) !== Boolean(mainSku.hideFreightCost)) {
    diffArr.push({
      name: 'hideFreightCost',
      title: i18n.t('platform.sku.form.label.hideFreightCost'),
      main: Boolean(mainSku.hideFreightCost) ? 'True' : 'False',
      draft: Boolean(sku.hideFreightCost) ? 'True' : 'False',
    });
  }

  // useUnitPriceMatrixFixedQuantity
  if (
    sku.useUnitPriceMatrixFixedQuantity !==
    mainSku.useUnitPriceMatrixFixedQuantity
  ) {
    diffArr.push({
      name: 'useUnitPriceMatrixFixedQuantity',
      title: i18n.t('platform.sku.form.label.useUnitPriceMatrixFixedQuantity'),
      main: Boolean(mainSku.useUnitPriceMatrixFixedQuantity) ? 'True' : 'False',
      draft: Boolean(sku.useUnitPriceMatrixFixedQuantity) ? 'True' : 'False',
    });
  }

  // customQuantityEnabled
  if (
    Boolean(sku.customQuantityEnabled) !==
    Boolean(mainSku.customQuantityEnabled)
  ) {
    diffArr.push({
      name: 'customQuantityEnabled',
      title: i18n.t('platform.sku.form.label.customQuantityEnabled'),
      main: Boolean(mainSku.customQuantityEnabled) ? 'True' : 'False',
      draft: Boolean(sku.customQuantityEnabled) ? 'True' : 'False',
    });
  }

  // customQuantities
  if (
    (sku.customQuantities || []).join(',') !==
    (mainSku.customQuantities || []).join(',')
  ) {
    diffArr.push({
      name: 'customQuantities',
      title: i18n.t('platform.sku.form.label.customQuantities'),
      main: (mainSku.customQuantities || []).join(', '),
      draft: (sku.customQuantities || []).join(', '),
    });
  }

  // hideQuantityMultipleVersion
  if (
    sku.hideQuantityMultipleVersion !== mainSku.hideQuantityMultipleVersion
  ) {
    diffArr.push({
      name: 'hideQuantityMultipleVersion',
      title: i18n.t('platform.sku.form.label.hideQuantityMultipleVersion'),
      main: Boolean(mainSku.hideQuantityMultipleVersion) ? 'True' : 'False',
      draft: Boolean(sku.hideQuantityMultipleVersion) ? 'True' : 'False',
    });
  }

  // proofNeeded
  if (sku.proofNeeded !== mainSku.proofNeeded) {
    diffArr.push({
      name: 'proofNeeded',
      title: i18n.t('platform.sku.form.label.proofNeeded'),
      main: Boolean(mainSku.proofNeeded) ? 'True' : 'False',
      draft: Boolean(sku.proofNeeded) ? 'True' : 'False',
    });
  }

  // skipSizeAndOrientation
  if (sku.skipSizeAndOrientation !== mainSku.skipSizeAndOrientation) {
    diffArr.push({
      name: 'skipSizeAndOrientation',
      title: i18n.t(
        'platform.sku.wizard.quantity.form.label.sizesOrientationSkip'
      ),
      main: Boolean(mainSku.skipSizeAndOrientation) ? 'True' : 'False',
      draft: Boolean(sku.skipSizeAndOrientation) ? 'True' : 'False',
    });
  }

  // skipInkAndFinish
  if (sku.skipInkAndFinish !== mainSku.skipInkAndFinish) {
    diffArr.push({
      name: 'skipInkAndFinish',
      title: i18n.t('platform.sku.wizard.quantity.form.label.inkFinishesSkip'),
      main: Boolean(mainSku.skipInkAndFinish) ? 'True' : 'False',
      draft: Boolean(sku.skipInkAndFinish) ? 'True' : 'False',
    });
  }

  // skipPaperOptions
  if (sku.skipPaperOptions !== mainSku.skipPaperOptions) {
    diffArr.push({
      name: 'skipPaperOptions',
      title: i18n.t('platform.sku.wizard.quantity.form.label.paperSkip'),
      main: Boolean(mainSku.skipPaperOptions) ? 'True' : 'False',
      draft: Boolean(sku.skipPaperOptions) ? 'True' : 'False',
    });
  }

  // hidePaperConstraintSection
  if (sku.hidePaperConstraintSection !== mainSku.hidePaperConstraintSection) {
    diffArr.push({
      name: 'hidePaperConstraintSection',
      title: i18n.t(
        'platform.sku.wizard.quantity.form.label.hidePaperConstraintSection'
      ),
      main: Boolean(mainSku.hidePaperConstraintSection) ? 'True' : 'False',
      draft: Boolean(sku.hidePaperConstraintSection) ? 'True' : 'False',
    });
  }

  // hidePaperSearchSection
  if (sku.hidePaperSearchSection !== mainSku.hidePaperSearchSection) {
    diffArr.push({
      name: 'hidePaperSearchSection',
      title: i18n.t(
        'platform.sku.wizard.quantity.form.label.hidePaperSearchSection'
      ),
      main: Boolean(mainSku.hidePaperSearchSection) ? 'True' : 'False',
      draft: Boolean(sku.hidePaperSearchSection) ? 'True' : 'False',
    });
  }

  // skipArtFile
  if (sku.skipArtFile !== mainSku.skipArtFile) {
    diffArr.push({
      name: 'skipArtFile',
      title: i18n.t('platform.sku.wizard.quantity.form.label.uploadArtFiles'),
      main: Boolean(mainSku.skipArtFile) ? 'True' : 'False',
      draft: Boolean(sku.skipArtFile) ? 'True' : 'False',
    });
  }

  // skip job options
  if (sku.skipJobOption !== mainSku.skipJobOption) {
    diffArr.push({
      name: 'skipJobOption',
      title: i18n.t('platform.sku.form.label.skipJobOption'),
      main: Boolean(mainSku.skipJobOption) ? 'True' : 'False',
      draft: Boolean(sku.skipJobOption) ? 'True' : 'False',
    });
  }

  // skip required service
  if (sku.skipRequiredSvc !== mainSku.skipRequiredSvc) {
    diffArr.push({
      name: 'skipRequiredSvc',
      title: i18n.t('platform.sku.form.label.skipRequiredSvc'),
      main: Boolean(mainSku.skipRequiredSvc) ? 'True' : 'False',
      draft: Boolean(sku.skipRequiredSvc) ? 'True' : 'False',
    });
  }

  // skip Additional job options
  if (sku.skipAdditionalJobOption !== mainSku.skipAdditionalJobOption) {
    diffArr.push({
      name: 'skipAdditionalJobOption',
      title: i18n.t('platform.sku.form.label.skipAdditionalJobOption'),
      main: Boolean(mainSku.skipAdditionalJobOption) ? 'True' : 'False',
      draft: Boolean(sku.skipAdditionalJobOption) ? 'True' : 'False',
    });
  }

  // skip optional service
  if (sku.skipOptionalSvc !== mainSku.skipOptionalSvc) {
    diffArr.push({
      name: 'skipOptionalSvc',
      title: i18n.t('platform.sku.form.label.skipOptionalSvc'),
      main: Boolean(mainSku.skipOptionalSvc) ? 'True' : 'False',
      draft: Boolean(sku.skipOptionalSvc) ? 'True' : 'False',
    });
  }

  // job options defs
  if (!isEqual(mainSku.jobOptionDefs || [], sku.jobOptionDefs || [])) {
    diffArr.push({
      name: 'jobOptionDefs',
      title: i18n.t('platform.sku.form.label.jobOptionDefs'),
      main: mainSku.jobOptionDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
      draft: sku.jobOptionDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
    });
  }

  // required services defs
  if (!isEqual(mainSku.requiredSvcDefs || [], sku.requiredSvcDefs || [])) {
    diffArr.push({
      name: 'requiredSvcDefs',
      title: i18n.t('platform.sku.form.label.requiredSvcDefs'),
      main: mainSku.requiredSvcDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
      draft: sku.requiredSvcDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
    });
  }

  // Optional Services defs
  if (!isEqual(mainSku.optionalSvcDefs || [], sku.optionalSvcDefs || [])) {
    diffArr.push({
      name: 'optionalSvcDefs',
      title: i18n.t('platform.sku.form.label.optionalSvcDefs'),
      main: mainSku.optionalSvcDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
      draft: sku.optionalSvcDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
    });
  }

  // Additional Job Options defs
  if (
    !isEqual(
      mainSku.additionalJobOptionDefs || [],
      sku.additionalJobOptionDefs || []
    )
  ) {
    diffArr.push({
      name: 'additionalJobOptionDefs',
      title: i18n.t('platform.sku.form.label.additionalJobOptionDefs'),
      main: mainSku.additionalJobOptionDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
      draft: sku.additionalJobOptionDefs
        ?.map((item) => item.name)
        .filter((item) => item)
        .join(', '),
    });
  }

  // job options
  if (
    showSvcNameList(mainSku.svcGroupUnion?.jobOptions) !==
    showSvcNameList(sku.svcGroupUnion?.jobOptions)
  ) {
    diffArr.push({
      name: 'jobOptions',
      title: i18n.t('platform.sku.form.label.jobOptions'),
      main: showSvcNameList(mainSku.svcGroupUnion?.jobOptions),
      draft: showSvcNameList(sku.svcGroupUnion?.jobOptions),
    });
  }

  // Required Services
  if (
    showSvcNameList(mainSku.svcGroupUnion?.requiredSvcGroup) !==
    showSvcNameList(sku.svcGroupUnion?.requiredSvcGroup)
  ) {
    diffArr.push({
      name: 'requiredServices',
      title: i18n.t('platform.sku.form.label.requiredServices'),
      main: showSvcNameList(mainSku.svcGroupUnion?.requiredSvcGroup),
      draft: showSvcNameList(sku.svcGroupUnion?.requiredSvcGroup),
    });
  }

  // Optional Services
  if (
    showSvcNameList(mainSku.svcGroupUnion?.optionalSvcGroup) !==
    showSvcNameList(sku.svcGroupUnion?.optionalSvcGroup)
  ) {
    diffArr.push({
      name: 'optionalServices',
      title: i18n.t('platform.sku.form.label.optionalServices'),
      main: showSvcNameList(mainSku.svcGroupUnion?.optionalSvcGroup),
      draft: showSvcNameList(sku.svcGroupUnion?.optionalSvcGroup),
    });
  }

  // Additional Job Options
  if (
    showSvcNameList(mainSku.svcGroupUnion?.additionalJobOptions) !==
    showSvcNameList(sku.svcGroupUnion?.additionalJobOptions)
  ) {
    diffArr.push({
      name: 'additionalJobOptions',
      title: i18n.t('platform.sku.form.label.additionalJobOptions'),
      main: showSvcNameList(mainSku.svcGroupUnion?.additionalJobOptions),
      draft: showSvcNameList(sku.svcGroupUnion?.additionalJobOptions),
    });
  }

  // Parcel Category
  if (
    sku.parcelCategories?.filter((item) => item)?.join(',') !==
    mainSku.parcelCategories?.filter((item) => item).join(',')
  ) {
    diffArr.push({
      name: 'parcelCategories',
      title: i18n.t('platform.sku.form.label.parcelCategories'),
      main: mainSku.parcelCategories
        ?.filter((item) => item)
        .map(
          (item) =>
            parcelCategoriesList.find((parcel) => parcel.value === item)?.title
        )
        .join(', '),
      draft: sku.parcelCategories
        ?.filter((item) => item)
        .map(
          (item) =>
            parcelCategoriesList.find((parcel) => parcel.value === item)?.title
        )
        .join(', '),
    });
  }

  // top gallery labels
  if (
    sku.topGalleryLabels?.filter((item) => item)?.join(',') !==
    mainSku.topGalleryLabels?.filter((item) => item).join(',')
  ) {
    diffArr.push({
      name: 'topGalleryLabels',
      title: i18n.t('platform.sku.form.label.topGalleryLabels'),
      main: mainSku.topGalleryLabels
        ?.filter((item) => item)
        .map((item) => topGalleryLabels[item])
        .join(', '),
      draft: sku.topGalleryLabels
        ?.filter((item) => item)
        .map((item) => topGalleryLabels[item])
        .join(', '),
    });
  }

  // gallery label id
  if (sku.galleryLabel?.id !== mainSku.galleryLabel?.id) {
    diffArr.push({
      name: 'galleryLabelId',
      title: i18n.t('platform.sku.form.label.galleryLabel'),
      main: mainSku.galleryLabel?.name,
      draft: sku.galleryLabel?.name,
    });
  }

  // company label id
  if (sku.companyLabel?.id !== mainSku.companyLabel?.id) {
    diffArr.push({
      name: 'companyLabelId',
      title: i18n.t('platform.sku.form.label.companyLabel'),
      main: mainSku.companyLabel?.name,
      draft: sku.companyLabel?.name,
    });
  }

  if (sku.qtyLocked !== mainSku.qtyLocked) {
    diffArr.push({
      name: 'qtyLocked',
      title: 'Quantity Locked',
      main: mainSku.qtyLocked,
      draft: sku.qtyLocked,
    });
  }

  // art files
  if (artFiles !== mainArtFiles) {
    diffArr.push({
      name: 'artFiles',
      title: i18n.t('platform.sku.form.label.artFiles'),
      main: mainArtFiles,
      draft: artFiles,
    });
  }

  // file display
  if (sku.artworkOption !== mainSku.artworkOption) {
    diffArr.push({
      name: 'artworkOption',
      title: i18n.t('platform.sku.form.label.artworkOption'),
      main: mainSku.artworkOption,
      draft: sku.artworkOption,
    });
  }

  // gallery image
  if (sku.images?.[0] !== mainSku.images?.[0]) {
    diffArr.push({
      name: 'galleryImage',
      title: i18n.t('platform.sku.form.label.galleryImage'),
      main: mainSku.images?.[0],
      draft: sku.images?.[0],
    });
  }

  if (!isTop) {
    // sku use
    if (sku.multiType !== mainSku.multiType) {
      diffArr.push({
        name: 'multiType',
        title: i18n.t('platform.sku.form.label.skuUse'),
        main: mainSku.multiType,
        draft: sku.multiType,
      });
    }
  }

  // inherit setting
  if (sku.inheritSetting !== mainSku.inheritSetting) {
    diffArr.push({
      name: 'inheritSetting',
      title: i18n.t('platform.sku.form.label.inheritUpdates'),
      main: mainSku.inheritSetting,
      draft: sku.inheritSetting,
    });
  }

  /* paperOption diff */
  if (!isEqual(sku.skuPaperOptions, mainSku.skuPaperOptions)) {
    diffArr.push({
      name: 'skuPaperOptions',
      title: i18n.t('platform.sku.form.label.skuPaperOptions'),
    });
  }

  if (sku.paperEquivalentCode !== mainSku.paperEquivalentCode) {
    diffArr.push({
      name: 'skuPaperOptions',
      title: i18n.t('platform.sku.form.label.paperEquivalentCode'),
    });
  }

  if (sku.isSpecifySupplier !== mainSku.isSpecifySupplier) {
    diffArr.push({
      name: 'isSpecifySupplier',
      title: 'isSpecifySupplier',
      main: mainSku.isSpecifySupplier,
      draft: sku.isSpecifySupplier,
    });
  }

  if (
    sku.specifySupplierOrgIds?.join(',') !==
    mainSku.specifySupplierOrgIds?.join(',')
  ) {
    diffArr.push({
      name: 'specifySupplierOrgIds',
      title: 'specifySupplierOrgIds',
      main: (mainSku.specifySupplierOrgIds || []).join(', '),
      draft: (sku.specifySupplierOrgIds || []).join(', '),
    });
  }

  if (!isTop) {
    // Quantity Rules
    const draftQtyRules = getQtyRulesString(sku);

    const mainQtyRules = getQtyRulesString(mainSku);
    if (draftQtyRules !== mainQtyRules) {
      diffArr.push({
        name: 'qtyRules',
        title: i18n.t('platform.sku.publishReview.optionTitle.qtyRules'),
        main: mainQtyRules,
        draft: draftQtyRules,
      });
    }

    // Sizes
    const draftSizes = getSizesString(sku, unit);
    const mainSizes = getSizesString(mainSku, unit);
    if (draftSizes !== mainSizes) {
      diffArr.push({
        name: 'sizes',
        title: i18n.t('platform.sku.form.label.sizes'),
        main: mainSizes,
        draft: draftSizes,
      });
    }

    // Predefined sizes - a selection of their own, so a change to them alone still counts as a
    // change to publish.
    const draftPresets = getPresetSizesString(sku);
    const mainPresets = getPresetSizesString(mainSku);
    if (draftPresets !== mainPresets) {
      diffArr.push({
        name: 'presetSizes',
        title: i18n.t('platform.sku.wizard.size.presetTitle'),
        main: mainPresets,
        draft: draftPresets,
      });
    }

    // Paper Rules
    const draftPaperRules = getPaperRulesString(sku);
    const mainPaperRules = getPaperRulesString(mainSku);
    if (draftPaperRules !== mainPaperRules) {
      diffArr.push({
        name: 'paperRules',
        title: i18n.t('platform.sku.form.label.paperRules'),
        main: mainPaperRules,
        draft: draftPaperRules,
      });
    }

    // Presses
    const draftPresses = getPressesString(sku, eqptTypes, exceptEqpts);
    const mainPresses = getPressesString(mainSku, eqptTypes, mainExceptEqpts);
    if (draftPresses !== mainPresses) {
      diffArr.push({
        name: 'presses',
        title: i18n.t('platform.sku.form.label.press'),
        main: mainPresses,
        draft: draftPresses,
      });
    }

    // Ink
    const draftInk = getInkString(sku);
    const mainInk = getInkString(mainSku);
    if (draftInk !== mainInk) {
      diffArr.push({
        name: 'ink',
        title: i18n.t('platform.sku.form.label.ink'),
        main: mainInk,
        draft: draftInk,
      });
    }

    // Required SVCs
    const draftSVCs = getRequireSvcString(sku);
    const mainSVCs = getRequireSvcString(mainSku);
    if (draftSVCs !== mainSVCs) {
      diffArr.push({
        name: 'requiredSvc',
        title: i18n.t('platform.sku.form.label.requiredServices'),
        main: mainSVCs,
        draft: draftSVCs,
      });
    }

    // Optional SVCs
    const draftOptSVCs = getOptionalSvcString(sku);
    const mainOptSVCs = getOptionalSvcString(mainSku);
    if (draftOptSVCs !== mainOptSVCs) {
      diffArr.push({
        name: 'optionalSvc',
        title: i18n.t('platform.sku.form.label.optionalServices'),
        main: mainOptSVCs,
        draft: draftOptSVCs,
      });
    }

    // Extra Spec
    const draftExtraSpec = getExtraSpecString(sku);
    const mainExtraSpec = getExtraSpecString(mainSku);
    if (draftExtraSpec !== mainExtraSpec) {
      diffArr.push({
        name: 'extraSpec',
        title: i18n.t('platform.sku.form.label.extraSpecValues'),
        main: mainExtraSpec,
        draft: draftExtraSpec,
      });
    }
  }

  // Multi SKU special diffs

  if (isTop) {
    // Sizes
    const draftSizes = getSizesString(sku, unit);
    const mainSizes = getSizesString(mainSku, unit);
    if (draftSizes !== mainSizes) {
      diffArr.push({
        name: 'sizes',
        title: i18n.t('platform.sku.form.label.sizes'),
        main: mainSizes,
        draft: draftSizes,
      });
    }

    // Predefined sizes - a selection of their own, so a change to them alone still counts as a
    // change to publish.
    const draftPresets = getPresetSizesString(sku);
    const mainPresets = getPresetSizesString(mainSku);
    if (draftPresets !== mainPresets) {
      diffArr.push({
        name: 'presetSizes',
        title: i18n.t('platform.sku.wizard.size.presetTitle'),
        main: mainPresets,
        draft: draftPresets,
      });
    }

    // Required SVCs
    const draftSVCs = getRequireSvcString(sku);
    const mainSVCs = getRequireSvcString(mainSku);
    if (draftSVCs !== mainSVCs) {
      diffArr.push({
        name: 'requiredSvc',
        title: i18n.t('platform.sku.form.label.requiredServices'),
        main: mainSVCs,
        draft: draftSVCs,
      });
    }

    // Optional SVCs
    const draftOptSVCs = getOptionalSvcString(sku);
    const mainOptSVCs = getOptionalSvcString(mainSku);
    if (draftOptSVCs !== mainOptSVCs) {
      diffArr.push({
        name: 'optionalSvc',
        title: i18n.t('platform.sku.form.label.optionalServices'),
        main: mainOptSVCs,
        draft: draftOptSVCs,
      });
    }
    // Quantity Rules
    const draftQtyRules = getMultiQtyRulesString(sku);
    const mainQtyRules = getMultiQtyRulesString(mainSku);
    if (draftQtyRules !== mainQtyRules) {
      diffArr.push({
        name: 'qtyRules',
        title: i18n.t('platform.sku.publishReview.optionTitle.qtyRules'),
        main: mainQtyRules,
        draft: draftQtyRules,
      });
    }

    // // Parts - Individual SKU
    // const draftPartsString = getPartsString(sku);
    // const mainPartsString = getPartsString(mainSku);
    // if (draftPartsString !== mainPartsString) {
    //   diffArr.push({
    //     name: 'parts',
    //     title: i18n.t('platform.sku.publishReview.optionTitle.individualSKUs'),
    //     main: mainPartsString,
    //     draft: draftPartsString,
    //   });
    // }

    // Parts Diffs for Sizes & Qty Dependency
    const parts = sortBy(sku.parts || [], (item) => item.sequence);
    const mainParts = sortBy(mainSku.parts || [], (item) => item.sequence);

    const maxPartLength = Math.max(parts.length, mainParts.length);

    for (let i = 0; i < maxPartLength; i++) {
      const mainPart = mainParts[i];
      const part = parts[i];
      const draftPartString = part ? getPartString(sku, part, unit) : '';
      const mainPartString = mainPart
        ? getPartString(mainSku, mainPart, unit)
        : '';
      if (draftPartString !== mainPartString) {
        const diff = {
          name: 'part' + (i + 1),
          title: 'Part ' + (i + 1),
          main: mainPartString,
          draft: draftPartString,
        };

        diffArr.push(diff);
      }
    }
  }

  return diffArr;
};

export const getPartString = (
  topSku: PartialSKU,
  skuPart: SKUPart,
  unit: MeasurementUnitEnum
) => {
  const items: string[] = [];
  items.push('Part Sequence: ' + skuPart.sequence);
  items.push('Sku Name: ' + skuPart.part.name);
  items.push(' ');
  items.push('Size Pairs:');
  items.push(getSKUSizePairString(skuPart, unit));

  items.push(' ');
  items.push(getQtyDependencyString(skuPart, topSku));
  return items.join('\n');
};
